import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  InputAdornment,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentsIcon from "@mui/icons-material/Payments";
import { styled } from "@mui/material/styles";

// Components
import BaseWrapper from "../../components/shared/BaseWrapper";

// API
import { getCacheTechnicians } from "../API/technicianAPI";
import { 
  getTechniciansPayrateCredits, 
  patchTechnicianPayrateCredits,
  postTechnicianPayrateCredits,
} from "../API/technicianPayrateCreditsAPI";

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  opacity: 0.8,
}));

function TechnicianCompensation() {
  const [technicians, setTechnicians] = useState([]);
  const [filteredTechnicians, setFilteredTechnicians] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showReplenishModal, setShowReplenishModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isInitializing, setIsInitializing] = useState(false);

  // Load technicians on mount and when returning to page
  useEffect(() => {
    loadTechnicians();

    // Reload data when user returns to this tab/window
    const handleFocus = () => {
      loadTechnicians();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadTechnicians = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      // Fetch technicians basic info and payrate-credits data in parallel
      const [techData, payrateData] = await Promise.all([
        getCacheTechnicians(),
        getTechniciansPayrateCredits()
      ]);

      if (techData && Array.isArray(techData)) {
        // Create a map of payrate data by tech_id for quick lookup
        const payrateMap = {};
        if (payrateData && Array.isArray(payrateData)) {
          payrateData.forEach(item => {
            // Convert tech_id to string for consistent lookup
            const techIdKey = String(item.tech_id);
            payrateMap[techIdKey] = {
              basicPayRate: parseFloat(item.basic_pay_rate) || 0,
              silCredits: parseFloat(item.leave_credits) || 0
            };
          });
        }

        // Find technicians without compensation records
        const techsNeedingInit = [];
        techData.forEach((tech) => {
          const techIdKey = String(tech.id);
          if (!payrateMap[techIdKey]) {
            techsNeedingInit.push(tech);
          }
        });

        // Auto-create missing records
        if (techsNeedingInit.length > 0) {
          const createPromises = techsNeedingInit.map((tech) =>
            postTechnicianPayrateCredits({
              tech_id: String(tech.id),
              basic_pay_rate: 0,
              leave_credits: 5,
            })
          );

          await Promise.all(createPromises);

          // Add newly created records to the map
          techsNeedingInit.forEach((tech) => {
            const techIdKey = String(tech.id);
            payrateMap[techIdKey] = {
              basicPayRate: 0,
              silCredits: 5
            };
          });
        }

        // Merge technician data with payrate-credits data
        // getCacheTechnicians returns: { id, name } not { firstname, lastname }
        const techWithCompensation = techData.map((tech) => {
          const techIdKey = String(tech.id);
          return {
            ...tech,
            basicPayRate: payrateMap[techIdKey]?.basicPayRate || 0,
            silCredits: payrateMap[techIdKey]?.silCredits || 0,
          };
        });
        
        setTechnicians(techWithCompensation);
        setFilteredTechnicians(techWithCompensation);
      }
    } catch (error) {
      console.error("Error loading technicians:", error);
      setErrorMessage("Failed to load technicians list.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredTechnicians(technicians);
    } else {
      const filtered = technicians.filter((tech) => {
        const techName = tech.name || '';
        return techName.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredTechnicians(filtered);
    }
  }, [searchTerm, technicians]);

  const handleEditRecords = () => {
    setIsEditMode(true);
    // Initialize editedValues with current technician data
    const initialValues = {};
    technicians.forEach((tech) => {
      initialValues[tech.id] = {
        basicPayRate: tech.basicPayRate || 0,
        silCredits: tech.silCredits || 0,
      };
    });
    setEditedValues(initialValues);
  };

  const handleCancelEdit = () => {
    // Show discard confirmation modal
    setShowDiscardModal(true);
  };

  const handleConfirmDiscard = () => {
    setShowDiscardModal(false);
    setIsEditMode(false);
    setEditedValues({});
  };

  const handleCloseDiscardModal = () => {
    setShowDiscardModal(false);
  };

  const handleSaveRecords = () => {
    // Show confirmation modal
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    setErrorMessage("");
    
    try {
      // Get all changed records
      const changedRecords = [];
      Object.keys(editedValues).forEach((techId) => {
        const original = technicians.find((t) => String(t.id) === String(techId));
        const edited = editedValues[techId];
        
        if (!original || !edited) return;
        
        const originalPayRate = parseFloat(original.basicPayRate || 0);
        const newPayRate = parseFloat(edited.basicPayRate || 0);
        const originalCredits = parseFloat(original.silCredits || 0);
        const newCredits = parseFloat(edited.silCredits || 0);
        
        if (
          originalPayRate.toFixed(2) !== newPayRate.toFixed(2) ||
          originalCredits.toFixed(1) !== newCredits.toFixed(1)
        ) {
          changedRecords.push({
            techId: techId,
            basicPayRate: newPayRate,
            silCredits: newCredits,
          });
        }
      });

      // Save all changed records
      const savePromises = changedRecords.map((record) =>
        patchTechnicianPayrateCredits(record.techId, {
          basic_pay_rate: record.basicPayRate,
          leave_credits: record.silCredits,
        })
      );

      const results = await Promise.all(savePromises);

      // Check if any save failed
      const failedSaves = results.filter(r => r === null);
      if (failedSaves.length > 0) {
        setErrorMessage(`Failed to save ${failedSaves.length} technician(s). Check console for details.`);
        setIsLoading(false);
        return;
      }

      // Update the technician list
      const updatedTechnicians = technicians.map((tech) => {
        if (editedValues[tech.id]) {
          return {
            ...tech,
            basicPayRate: parseFloat(editedValues[tech.id].basicPayRate) || 0,
            silCredits: parseFloat(editedValues[tech.id].silCredits) || 0,
          };
        }
        return tech;
      });

      setTechnicians(updatedTechnicians);
      setFilteredTechnicians(
        updatedTechnicians.filter((tech) => {
          const techName = tech.name || '';
          return (
            searchTerm === "" || techName.toLowerCase().includes(searchTerm.toLowerCase())
          );
        })
      );

      setIsEditMode(false);
      setEditedValues({});
      setSuccessMessage(
        `Successfully updated ${changedRecords.length} technician(s) compensation.`
      );
    } catch (error) {
      console.error("Error saving technician compensation:", error);
      setErrorMessage("Failed to save technician compensation.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
  };

  const handleInputChange = (techId, field, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [techId]: {
        ...prev[techId],
        [field]: value,
      },
    }));
  };

  // Get list of changed records for confirmation modal
  const getChangedRecords = () => {
    const changed = [];
    Object.keys(editedValues).forEach((techId) => {
      const original = technicians.find((t) => String(t.id) === String(techId));
      const edited = editedValues[techId];
      
      // Skip if technician not found
      if (!original || !edited) return;
      
      const originalPayRate = parseFloat(original.basicPayRate || 0);
      const newPayRate = parseFloat(edited.basicPayRate || 0);
      const originalCredits = parseFloat(original.silCredits || 0);
      const newCredits = parseFloat(edited.silCredits || 0);
      
      // Use toFixed for comparison to avoid floating point issues
      if (
        originalPayRate.toFixed(2) !== newPayRate.toFixed(2) ||
        originalCredits.toFixed(1) !== newCredits.toFixed(1)
      ) {
        changed.push({
          id: techId,
          name: original.name || 'Unknown',
          originalPayRate: originalPayRate,
          newPayRate: newPayRate,
          originalCredits: originalCredits,
          newCredits: newCredits,
        });
      }
    });
    return changed;
  };

  const handleInitializeRecords = async () => {
    setIsInitializing(true);
    setErrorMessage("");
    
    try {
      // Find technicians without compensation records
      const techsNeedingInit = technicians.filter(
        (tech) => tech.basicPayRate === 0 && tech.silCredits === 0
      );

      if (techsNeedingInit.length === 0) {
        setSuccessMessage("All technicians already have compensation records.");
        setIsInitializing(false);
        return;
      }

      // Create records for each technician
      const createPromises = techsNeedingInit.map((tech) =>
        postTechnicianPayrateCredits({
          tech_id: String(tech.id),
          basic_pay_rate: 0,
          leave_credits: 5,
        })
      );

      await Promise.all(createPromises);

      // Reload the data
      await loadTechnicians();

      setSuccessMessage(
        `Successfully initialized ${techsNeedingInit.length} technician compensation record(s).`
      );
    } catch (error) {
      console.error("Error initializing records:", error);
      setErrorMessage("Failed to initialize technician records.");
    } finally {
      setIsInitializing(false);
    }
  };

  const handleReplenishSilCredits = async () => {
    setShowReplenishModal(true);
  };

  const confirmReplenishSilCredits = async () => {
    setShowReplenishModal(false);
    setIsLoading(true);
    setErrorMessage("");
    
    try {
      const updatePromises = technicians.map((tech) =>
        patchTechnicianPayrateCredits(tech.id, {
          basic_pay_rate: tech.basicPayRate,
          leave_credits: 5,
        })
      );

      await Promise.all(updatePromises);

      // Reload the data
      await loadTechnicians();

      setSuccessMessage(
        `Successfully replenished SIL credits for all ${technicians.length} technician(s).`
      );
    } catch (error) {
      console.error("Error replenishing SIL credits:", error);
      setErrorMessage("Failed to replenish SIL credits.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseWrapper
      titleIcon={<PaymentsIcon />}
      title="Payroll"
      subTitle="Technician Compensation"
      subTitleRoute={"/technician-compensation"}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            Manage basic pay rates and Service Incentive Leave (SIL) credits for technicians
          </Typography>
        </Grid>

        {/* Search Bar */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by technician name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Edit/Save Records Button */}
        <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            color="success"
            onClick={handleReplenishSilCredits}
            disabled={isLoading || isEditMode || technicians.length === 0}
          >
            Replenish SIL Credits
          </Button>
          {!isEditMode ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditRecords}
              disabled={isLoading || filteredTechnicians.length === 0}
            >
              Edit Records
            </Button>
          ) : (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveRecords}
              >
                Save Records
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      <StyledHr />

      {/* Table */}
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: "bold" }} align="center">
                Technician Name
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="center">
                Basic Pay Rate (₱/hour)
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="center">
                SIL Credits (days)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTechnicians.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1" color="text.secondary">
                    {isLoading ? "Loading technicians..." : "No technicians found"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTechnicians.map((tech) => (
                <TableRow key={tech.id} hover>
                  <TableCell align="center">
                    {tech.name}
                  </TableCell>
                  <TableCell align="center">
                    {isEditMode ? (
                      <TextField
                        type="number"
                        size="small"
                        value={editedValues[tech.id]?.basicPayRate || 0}
                        onChange={(e) =>
                          handleInputChange(tech.id, "basicPayRate", e.target.value)
                        }
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: 120 }}
                      />
                    ) : (
                      <Typography>
                        ₱{parseFloat(tech.basicPayRate || 0).toFixed(2)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isEditMode ? (
                      <TextField
                        type="number"
                        size="small"
                        value={editedValues[tech.id]?.silCredits || 0}
                        onChange={(e) =>
                          handleInputChange(tech.id, "silCredits", e.target.value)
                        }
                        onFocus={(e) => e.target.select()}
                        inputProps={{ min: 0, step: 0.5 }}
                        sx={{ width: 100 }}
                      />
                    ) : (
                      <Typography>
                        {parseFloat(tech.silCredits || 0).toFixed(1)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      {!isLoading && filteredTechnicians.length > 0 && (
        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredTechnicians.length} technician(s)
          </Typography>
        </Box>
      )}

      {/* Confirmation Modal */}
      <Dialog
        open={showConfirmModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please review the following changes before saving:
          </Typography>
          {getChangedRecords().length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
              No changes detected.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">Technician</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      Basic Pay Rate
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      SIL Credits
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getChangedRecords().map((record) => (
                    <TableRow key={record.id}>
                      <TableCell align="center">{record.name}</TableCell>
                      <TableCell align="center">
                        {record.originalPayRate !== record.newPayRate ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ textDecoration: "line-through", color: "text.secondary" }}
                            >
                              ₱{record.originalPayRate.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "success.main", fontWeight: "bold" }}>
                              ₱{record.newPayRate.toFixed(2)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            ₱{record.originalPayRate.toFixed(2)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {record.originalCredits !== record.newCredits ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ textDecoration: "line-through", color: "text.secondary" }}
                            >
                              {record.originalCredits.toFixed(1)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "success.main", fontWeight: "bold" }}>
                              {record.newCredits.toFixed(1)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {record.originalCredits.toFixed(1)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Typography variant="body2" sx={{ mt: 2, fontWeight: "bold" }}>
            Total changes: {getChangedRecords().length} technician(s)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="error">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            color="primary"
            variant="contained"
            disabled={getChangedRecords().length === 0}
            startIcon={<SaveIcon />}
          >
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard Changes Confirmation Modal */}
      <Dialog
        open={showDiscardModal}
        onClose={handleCloseDiscardModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>Discard Changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to discard your changes? The following modifications will be lost:
          </Typography>
          {getChangedRecords().length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
              No changes detected.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">Technician</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      Basic Pay Rate
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      SIL Credits
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getChangedRecords().map((record) => (
                    <TableRow key={record.id}>
                      <TableCell align="center">{record.name}</TableCell>
                      <TableCell align="center">
                        {record.originalPayRate !== record.newPayRate ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ textDecoration: "line-through", color: "text.secondary" }}
                            >
                              ₱{record.originalPayRate.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "warning.main", fontWeight: "bold" }}>
                              ₱{record.newPayRate.toFixed(2)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            ₱{record.originalPayRate.toFixed(2)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {record.originalCredits !== record.newCredits ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ textDecoration: "line-through", color: "text.secondary" }}
                            >
                              {record.originalCredits.toFixed(1)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "warning.main", fontWeight: "bold" }}>
                              {record.newCredits.toFixed(1)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {record.originalCredits.toFixed(1)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Typography variant="body2" sx={{ mt: 2, fontWeight: "bold", color: "error.main" }}>
            {getChangedRecords().length > 0 ? `${getChangedRecords().length} change(s) will be discarded` : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDiscardModal} color="primary" variant="contained">
            Keep Editing
          </Button>
          <Button
            onClick={handleConfirmDiscard}
            color="error"
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replenish SIL Credits Modal */}
      <Dialog open={showReplenishModal} onClose={() => setShowReplenishModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#1e5d45", color: "white" }}>
          Replenish SIL Credits
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to replenish SIL credits for all <strong>{technicians.length} technician(s)</strong> to <strong>5.0 days</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: "warning.main", fontWeight: "bold" }}>
            ⚠️ This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReplenishModal(false)} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={confirmReplenishSilCredits}
            color="success"
            variant="contained"
            disabled={isLoading}
          >
            Confirm Replenish
          </Button>
        </DialogActions>
      </Dialog>
    </BaseWrapper>
  );
}

export default TechnicianCompensation;
